class HomepageSlider extends HTMLElement {
  constructor() {
    super();
    this.initSliderElements();
    if (!this.slider || this.sliderItems.length === 0) return;

    this.initPages();
    new ResizeObserver(() => this.initPages()).observe(this.slider);

    this.addEventListeners();
  }

  initSliderElements() {
    this.slider = this.querySelector('[id^="Slider-"]');
    this.sliderItems = this.querySelectorAll('[id^="Slide-"]');
    this.currentPageElement = this.querySelector('.slider-counter--current');
    this.pageTotalElement = this.querySelector('.slider-counter--total');
    this.dotContainer = this.querySelector('.slider-dots');
    this.currentSlide = 0;
  }

  addEventListeners() {
    this.slider.addEventListener('scroll', this.update.bind(this));
    ['pointerdown', 'pointermove', 'pointerup', 'pointerleave'].forEach(
      (event) =>
        document.addEventListener(
          event,
          this[`handle${event.charAt(0).toUpperCase() + event.slice(1)}`].bind(
            this
          )
        )
    );
  }

  initPages() {
    this.sliderItemsToShow = Array.from(this.sliderItems).filter(
      (element) => element.clientWidth > 0
    );
    this.calculateSlidesPerPage();
    this.populateDots();
    this.update();
  }

  calculateSlidesPerPage() {
    if (this.sliderItemsToShow.length === 0) return;

    // Assuming all slides are of the same width
    const slideWidth = this.sliderItemsToShow[0].offsetWidth;
    this.slidesPerPage = Math.max(
      1,
      Math.floor(this.slider.clientWidth / slideWidth)
    );
    this.totalPages = Math.ceil(
      this.sliderItemsToShow.length / this.slidesPerPage
    );
  }

  populateDots() {
    if (!this.dotContainer) return;
    this.dotContainer.innerHTML = '';
    for (let i = 0; i < this.totalPages; i++) {
      const dot = document.createElement('span');
      dot.className = `slider-dot${i === this.currentSlide ? ' active' : ''}`;
      dot.dataset.slideIndex = i;
      dot.addEventListener('click', this.onDotClick.bind(this));
      this.dotContainer.appendChild(dot);
    }
  }

  gotoSlide(slide) {
    // Calculate maximum slide index (considering full pages)
    const maxFullPageIndex = Math.floor(
      (this.sliderItems.length - 1) / this.slidesPerPage
    );
    const maxSlideIndex = Math.min(slide, maxFullPageIndex);

    // Calculate the new scroll position
    const slideWidth = this.sliderItems[0].offsetWidth;
    const newScrollPosition = maxSlideIndex * slideWidth * this.slidesPerPage;

    // Scroll to the new position
    this.slider.scrollTo({
      left: newScrollPosition,
      behavior: 'smooth',
    });

    // Update the current slide and refresh the UI
    this.currentSlide = maxSlideIndex;
    this.update();
  }

  onDotClick(event) {
    const slideIndex = parseInt(event.currentTarget.dataset.slideIndex, 10);
    this.gotoSlide(slideIndex);
  }

  handlePointerDown(event) {
    if (event.pointerType !== 'mouse') return;
    event.preventDefault();
    this.dragging = true;
    this.dragStartX = event.clientX;
    this.dragStartScrollLeft = this.slider.scrollLeft;
  }

  handlePointerMove(event) {
    if (!this.dragging || event.pointerType !== 'mouse') return;

    const dragDistance = event.clientX - this.dragStartX;
    this.slider.scrollLeft = this.dragStartScrollLeft - dragDistance;
  }

  handlePointerUp(event) {
    if (!this.dragging || event.pointerType !== 'mouse') return;

    this.dragging = false;
    const dragDistance = this.slider.scrollLeft - this.dragStartScrollLeft;
    const dragThreshold = this.slider.clientWidth * 0.2; // 20% of the slider width

    if (dragDistance > dragThreshold) {
      this.gotoSlide(this.currentSlide - 1);
    } else if (dragDistance < -dragThreshold) {
      this.gotoSlide(this.currentSlide + 1);
    }
  }

  handlePointerLeave(event) {
    if (this.dragging && event.pointerType === 'mouse') {
      this.dragging = false;
      this.handlePointerUp(event); // Reuse the pointer up logic
    }
  }

  update() {
    if (!this.slider) return;

    const totalScrollWidth = this.slider.scrollWidth - this.slider.clientWidth;
    const scrollRatio = this.slider.scrollLeft / totalScrollWidth;
    const newPageIndex = Math.round(scrollRatio * (this.totalPages - 1));

    if (this.currentSlide !== newPageIndex) {
      this.currentSlide = newPageIndex;
      this.updateDotNavigation();
      this.updatePageCounter();
    }
  }

  updatePageCounter() {
    if (this.currentPageElement && this.pageTotalElement) {
      // Since currentSlide is 0-indexed, add 1 for user-friendly numbering
      this.currentPageElement.textContent = this.currentSlide + 1;
      this.pageTotalElement.textContent = this.totalPages;
    }
  }

  updateDotNavigation() {
    const dots = this.dotContainer.querySelectorAll('.slider-dot');
    dots.forEach((dot, index) => {
      dot.classList.toggle('active', index === this.currentSlide);
    });
  }
}

customElements.define('homepage-slider', HomepageSlider);
